import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import { authApi, ApiError } from './api/client';
import { LoginPage } from './pages/LoginPage';
import { TwoFactorPage } from './pages/TwoFactorPage';
import { DashboardPage } from './pages/DashboardPage';
import { TenantsListPage } from './pages/TenantsListPage';
import { TenantDetailPage } from './pages/TenantDetailPage';
import { TenantCreatePage } from './pages/TenantCreatePage';
import { BrandExtractionPage } from './pages/BrandExtractionPage';
import { OfferingsExtractionPage } from './pages/OfferingsExtractionPage';
import { TenantProvisioningPage } from './pages/TenantProvisioningPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { Layout } from './components/Layout';

export function App() {
  const state = useAuthStore((s) => s.state);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);

  // On first mount, try /me to see if there's a live session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await authApi.me();
        if (!cancelled) setAuthenticated(me);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setUnauthenticated();
        } else {
          setUnauthenticated('Could not reach the server. Try again.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [setAuthenticated, setUnauthenticated]);

  if (state.status === 'loading') {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p className="text-ink-muted text-sm">Loading…</p>
      </main>
    );
  }

  if (state.status === 'unauthenticated') return <LoginPage />;
  if (state.status === 'challenge') return <TwoFactorPage />;

  // Authenticated — but if the admin is on a temporary password, force the
  // change-password screen as the ONLY reachable route until they update it.
  // The backend invalidates all sessions on a successful change, so the
  // ChangePasswordPage hands them back to the login screen automatically.
  if (state.me.must_change_password) {
    return <ChangePasswordPage forced />;
  }

  // Authenticated — full router.
  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/tenants" element={<TenantsListPage />} />
          <Route path="/tenants/new" element={<TenantCreatePage />} />
          <Route path="/tenants/:id" element={<TenantDetailPage />} />
          <Route path="/tenants/:id/brand-extraction" element={<BrandExtractionPage />} />
          <Route path="/tenants/:id/offerings-extraction" element={<OfferingsExtractionPage />} />
          <Route path="/tenants/:id/provisioning" element={<TenantProvisioningPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}
